import { createContext, useContext, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'ca-family-scope';

/** "self" (padrão) | "family" (agregado) | `userId` de um membro específico (0011). */
export type FamilyScope = string;

interface FamilyScopeContextValue {
  scope: FamilyScope;
  setScope: (scope: FamilyScope) => void;
}

const FamilyScopeContext = createContext<FamilyScopeContextValue | undefined>(undefined);

function readInitialScope(): FamilyScope {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? 'self';
  } catch {
    return 'self';
  }
}

/** Escopo de visualização de família (0011) — persiste na aba atual (sessionStorage),
 * global o bastante pra sobreviver à navegação entre rotas (cada rota renderiza seu
 * próprio `AuthenticatedShell`, então o estado precisa viver acima do router). */
export function FamilyScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<FamilyScope>(readInitialScope);

  function setScope(next: FamilyScope) {
    setScopeState(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Sem sessionStorage (modo privado, etc.) — segue só em memória.
    }
  }

  return <FamilyScopeContext.Provider value={{ scope, setScope }}>{children}</FamilyScopeContext.Provider>;
}

export function useFamilyScope(): FamilyScopeContextValue {
  const ctx = useContext(FamilyScopeContext);
  if (!ctx) throw new Error('useFamilyScope precisa de FamilyScopeProvider');
  return ctx;
}
